/**
 * Stats — the long view. Built to be worth reading on a bad day as well as a
 * good one, so it leads with the streak and the grade spread rather than a
 * single number that a Miss can wipe out.
 */

import { h } from '../dom';
import { ctx } from '../ctx';
import { GRADE_ORDER, GRADES } from '../../game/grade';
import { averageDistance, loadResults, recent } from '../../state/store';
import { isMuted, setMuted } from '../../state/store';
import { clearAll } from '../../state/store';
import { toast } from '../chrome';
import * as sfx from '../audio';
import { swatch } from '../paint';
import { longDate } from '../../core/schedule';

export function statsSheet(close: () => void): HTMLElement[] {
  const stats = ctx.stats;
  const total = Math.max(1, stats.played);
  const best = Object.entries(stats.grades).reduce(
    (acc, [g, n]) => (n > acc.n ? { g: g as keyof typeof stats.grades, n } : acc),
    { g: 'perfect' as keyof typeof stats.grades, n: -1 },
  );

  return [
    h(
      'div',
      { class: 'section' },
      h(
        'div',
        { class: 'grid-2' },
        big(String(stats.streak), 'current streak', true),
        big(String(stats.maxStreak), 'best streak'),
      ),
      h(
        'div',
        { class: 'grid-2' },
        big(stats.played ? `${Math.round((stats.wins / stats.played) * 100)}%` : '—', 'win rate'),
        big(
          averageDistance(stats) === null ? '—' : averageDistance(stats)!.toFixed(1),
          'avg. miss distance',
        ),
      ),
    ),

    h('div', { class: 'rule', style: { margin: 'var(--s-5) 0' } }),

    h('h2', { class: 'caps', text: 'Grade spread' }),
    h(
      'div',
      { class: 'dist' },
      ...GRADE_ORDER.map((g) =>
        h(
          'div',
          { class: 'dist__row' },
          h('div', { class: 'dist__k', text: GRADES[g].label }),
          h(
            'div',
            { class: 'dist__track' },
            h('div', {
              class: 'dist__fill',
              style: { '--bar': `var(--${GRADES[g].token})`, width: `${(stats.grades[g] / total) * 100}%` },
            }),
          ),
          h('div', { class: 'dist__n', text: String(stats.grades[g]) }),
        ),
      ),
    ),

    h('div', { class: 'rule', style: { margin: 'var(--s-5) 0' } }),

    ...(stats.bestDistance !== null
      ? [
          h(
            'div',
            { class: 'rows' },
            h(
              'div',
              { class: 'row' },
              h('div', { class: 'row__k', text: 'Tightest ever' }),
              h('div', { class: 'row__v', text: stats.bestDistance.toFixed(2) }),
            ),
            h(
              'div',
              { class: 'row' },
              h('div', { class: 'row__k', text: 'Most common' }),
              h('div', { class: 'row__v', text: best.n > 0 ? GRADES[best.g].label : '—' }),
            ),
          ),
        ]
      : []),

    h('div', { style: { height: 'var(--s-5)' } }),
    h('h2', { class: 'caps', text: 'Last 30 days' }),
    calendar(),

    h('div', { class: 'rule', style: { margin: 'var(--s-5) 0' } }),
    h(
      'div',
      { class: 'stack' },
      h(
        'button',
        {
          class: 'btn btn--wide',
          attrs: { type: 'button' },
          text: isMuted() ? '🔇  Sound off' : '🔊  Sound on',
          on: {
            click: (e: Event) => {
              const next = !isMuted();
              setMuted(next);
              sfx.setMuted(next);
              if (!next) sfx.sSelect();
              (e.currentTarget as HTMLButtonElement).textContent = next ? '🔇  Sound off' : '🔊  Sound on';
            },
          },
        },
      ),
      h('button', {
        class: 'btn btn--wide',
        attrs: { type: 'button' },
        text: 'Reset everything',
        on: {
          click: () => {
            clearAll();
            ctx.refresh();
            close();
            toast('Stats cleared');
          },
        },
      }),
    ),

    h('div', { style: { height: 'var(--s-4)' } }),
    h('button', { class: 'btn btn--primary btn--wide', text: 'Close', attrs: { type: 'button' }, on: { click: close } }),
  ];
}

function big(value: string, key: string, gold = false): HTMLElement {
  return h(
    'div',
    { class: 'stat', style: { padding: 'var(--s-4)' }, data: { accent: gold ? 'gold' : undefined } },
    h('div', { class: 'stat__n', style: { fontSize: 'var(--t-2xl)' }, text: value }),
    h('div', { class: 'stat__k', text: key }),
  );
}

/** Thirty tiles, newest last. A streak should look long. */
function calendar(): HTMLElement {
  const days = recent(30, ctx.index).slice().reverse();
  const results = new Map(loadResults().map((r) => [r.index, r]));

  return h(
    'div',
    { class: 'grid-30' },
    ...days.map((day) => {
      const result = results.get(day.index);
      const title = `${longDate(day.index)}${result ? ` — ${GRADES[result.grade].label}` : ' — not played'}`;
      return h('div', {
        class: 'cal-day',
        style: result ? swatch(result.hex) : undefined,
        data: { state: result ? (result.grade === 'miss' ? 'miss' : 'win') : 'empty' },
        attrs: { title, 'aria-label': title },
      });
    }),
  );
}
